import { property } from 'lodash';
import React, { useCallback } from 'react';
import DashboardComponent from '../Reports/Dashboard';
import Notification from '../Notification';
import useResourceLoader from 'src/util/useResourceLoader';
import ReportsApi from 'src/api/ReportsApi';

function Dashboard({ element }) {
  const { dashboardUuid = '', uiOptions } = element;

  const [guestToken, loadingGuestToken, errorLoadingGuestToken] = useResourceLoader(
    ac => ReportsApi.getDashboardEmbedToken({dashboardUuid}, ac),
    [dashboardUuid]
  );

  // Re-mint on demand: the embed SDK calls this as the current token nears expiry (guest tokens
  // carry a 300s TTL), and replaying the first token instead would 401 every request from the
  // 5-minute mark on. Deliberately not abortable - the SDK owns this call's lifetime, not the
  // component's, and an aborted refresh is the same dead embed we are fixing.
  const refetchToken = useCallback(
    async () => (await ReportsApi.getDashboardEmbedToken({ dashboardUuid })).token,
    [dashboardUuid]
  );

  return (
    <div className={`dashboard ${element[':className'] ?? ''}`}>
      {dashboardUuid ? (
        <DashboardComponent
          guestToken={guestToken}
          loadingGuestToken={loadingGuestToken}
          errorLoadingGuestToken={errorLoadingGuestToken}
          fetchToken={refetchToken}
          {...uiOptions}
        />
      ) : null}
    </div>
  );
}

Dashboard.rootSchema = {
  properties: {
    dashboardUuid: {
      title: 'Dashboard',
      lookup: {
        resource: 'Dashboards',
        allowNoValue: false,
        firstSelected: true,
        api: 'api:manage/reports/dashboards/list',
        jnx: 'data',
        id: 'nativeUuid',
        label: 'name',
      },
      'ui:field': 'LookupFormField',
      'map:field': 'dashboardUuid',
    },
    params: {
      title: 'Dashboard Parameters',
      ':classNames': 'from-col-1 to-col-4 smallThreeRows',
      'ui:expandable': true,
      'ui:sectionType': 'label',
      'ui:expandedDefault': true,
      type: 'object',
      properties: {
        uiOptions: {
          title: 'UI Options',
          type: 'object',
          'ui:sectionType': 'label',
          properties: {
            hideTitle: { title: 'Hide Title', type: 'boolean', 'map:field': 'uiOptions.hideTitle' },
            hideTab: { title: 'Hide Tabs', type: 'boolean', 'map:field': 'uiOptions.hideTab' },
            hideChartControls: { title: 'Hide Chart Controls', type: 'boolean', 'map:field': 'uiOptions.hideChartControls' },
          },
        },
        filters: {
          type: 'object',
          title: 'Extra Filters',
          'ui:sectionType': 'label',
          'ui:keyName': 'Filter',
          'ui:valueName': 'Value',
          'ui:asPropertiesTable': true,
          'map:field': 'uiOptions.filters',
          properties: {},
          additionalProperties: { type: 'string' },
        },
        urlParams: {
          type: 'object',
          title: 'URL Params',
          'ui:sectionType': 'label',
          'ui:keyName': 'Param',
          'ui:valueName': 'Value',
          'ui:asPropertiesTable': true,
          'map:field': 'uiOptions.urlParams',
          properties: {},
          additionalProperties: { type: 'string' },
        },
      },
    },
  },
};

function NewlineText(props) {
  return props.text.split('\n').map((str, idx) => <p key={idx}>{str}</p>);
}

export default Dashboard;
